import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { NavItem } from '../../types';
import { Button } from '../ui/Button';
import { db } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

interface ExtendedNavItem {
  label: string;
  path: string;
  isExternal?: boolean;
}

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginUrl, setLoginUrl] = useState('https://portal.realta.media');
  const location = useLocation();

  const navItems: ExtendedNavItem[] = [
    { label: 'Home', path: '/' },
    { label: 'Listing Media', path: '/listing-media' },
    { label: 'Brand Media', path: '/brand-media' },
    { label: 'About', path: '/about' },
    { label: 'Blog', path: '/blog' },
    { label: 'Login', path: loginUrl, isExternal: true },
  ];

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'siteContent', 'global-settings'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.loginUrl) {
          setLoginUrl(data.loginUrl);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }
  }, [isOpen]);

  // Desktop exclusions for visual balance
  const desktopExclusions = ['About', 'Blog'];

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-in-out ${
          isOpen 
            ? 'bg-black py-6 md:py-4' 
            : scrolled 
              ? 'bg-black/95 backdrop-blur-xl py-5 md:py-3' 
              : 'bg-gradient-to-b from-black/90 via-black/40 to-transparent py-9 md:py-6'
        }`}
      >
        <div className="max-w-[1920px] mx-auto px-10 md:px-12">
          <div className="flex justify-between items-center">
            {/* Logo Lockup */}
            <NavLink to="/" className="flex items-center gap-2 md:gap-3 group relative z-[101] flex-shrink-0" aria-label="Go to Home">
              <motion.div 
                className="relative h-8 md:h-10 flex items-center"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <img 
                  src="https://realta.b-cdn.net/Media/Realta-Icon%20Logo%202025%20Blue%20Blue.svg" 
                  alt="Realta Media Logo" 
                  width="40"
                  height="40"
                  decoding="async"
                  className="h-[102%] w-auto object-contain"
                />
              </motion.div>
              <div className="flex flex-col justify-center h-8 md:h-10 select-none leading-none w-fit">
                <span className="font-sans font-black text-white text-[1.4rem] md:text-[2.02rem] leading-[0.8] tracking-[0.08em] group-hover:text-brand-500 transition-colors duration-500 uppercase">REALTA</span>
                <div className="flex justify-between w-full mt-[3px] md:mt-[4px]">
                  {"MEDIA AGENCY".split('').map((char, i) => (
                    <span key={i} className="font-sans font-medium text-zinc-300 text-[6px] md:text-[9px] leading-none uppercase">{char === ' ' ? '\u00A0' : char}</span>
                  ))}
                </div>
              </div>
            </NavLink>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center space-x-10">
              {navItems.filter(item => !desktopExclusions.includes(item.label)).map((item) => (
                item.isExternal ? (
                  <a
                    key={item.label}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-display font-bold uppercase tracking-[0.15em] text-zinc-300 hover:text-white transition-all"
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) => `text-sm font-display font-bold uppercase tracking-[0.15em] transition-all hover:text-white ${isActive ? 'text-brand-500' : 'text-zinc-300'}`}
                  >
                    {item.label}
                  </NavLink>
                )
              ))}
              <NavLink to="/booking" className="relative group">
                <motion.div 
                  whileHover="hover" 
                  className="relative group p-[1px]"
                >
                  <div className="dot-capture-area" aria-hidden="true">
                    <div className="orbit-dot" />
                  </div>
                  <button className="relative px-8 py-3 text-sm font-display font-bold uppercase tracking-[0.25em] transition-all duration-500 rounded-[6px] bg-[linear-gradient(to_bottom,rgba(0,0,0,0.4)_0%,rgba(0,0,0,0.1)_100%)] backdrop-blur-xl border border-white text-white group-hover:bg-brand-500 group-hover:text-black group-hover:border-brand-500">
                    <span className="relative z-30">BOOK NOW</span>
                  </button>
                </motion.div>
              </NavLink>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden z-[101]">
              <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-white" aria-label={isOpen ? "Close menu" : "Open menu"}>
                {isOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-0 w-screen h-screen bg-black z-[100] lg:hidden flex flex-col items-center justify-center space-y-8 px-6"
            >
              <div className="flex flex-col items-center space-y-6 md:space-y-8">
                {/* Mobile Menu includes Home, Listing Media, Brand Media, and Login */}
                {navItems.filter(item => !['About', 'Blog'].includes(item.label)).map((item) => (
                  item.isExternal ? (
                    <a
                      key={item.label}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsOpen(false)}
                      className="text-2xl md:text-3xl font-display font-bold uppercase text-white hover:text-brand-500 transition-all"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <NavLink 
                      key={item.path} 
                      to={item.path} 
                      end={item.path === '/'}
                      onClick={() => setIsOpen(false)} 
                      className={({ isActive }) => `text-2xl md:text-3xl font-display font-bold uppercase transition-all ${isActive ? 'text-brand-500' : 'text-white hover:text-brand-500'}`}
                    >
                      {item.label}
                    </NavLink>
                  )
                ))}
                
                <NavLink to="/booking" onClick={() => setIsOpen(false)} className="mt-8">
                  <Button variant="primary" size="lg" className="w-full min-w-[200px] md:min-w-[260px] h-16 text-sm">
                    BOOK NOW
                  </Button>
                </NavLink>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};